import { type TrenchioByteStream } from "./trenchioByteStream";

/**
 * Player emblems: a drawing the player makes in the loadout menu, shown on top of the uniform.
 *
 * An emblem is a short list of drawing operations (pencil strokes, circles) on a 256x256 canvas
 * whose inscribed circle is the player's body. Clients rebuild the same picture from the same list,
 * so the network only carries a few hundred bytes and the server can validate every value.
 */

export const EMBLEM = {
    /** canvas size; all coordinates are integers in [0, size) */
    size: 256,
    maxOps: 32,
    maxPointsPerStroke: 150,
    maxPointsTotal: 500,
    minWidth: 1,
    maxWidth: 40,
    maxRadius: 128
} as const;

export const enum EmblemOpType { Pencil = 0, Circle = 1, Eraser = 2, Rect = 3 }

// Plain mutable shapes on purpose: packet data types are mapped through Mutable<> helpers.
export interface EmblemStroke {
    t: EmblemOpType.Pencil | EmblemOpType.Eraser
    /** 24-bit RGB colour */
    c: number
    /** line width in canvas pixels */
    w: number
    /** flat list of points: x0, y0, x1, y1, ... */
    p: number[]
}

export interface EmblemCircle {
    t: EmblemOpType.Circle
    c: number
    x: number
    y: number
    r: number
    /** true = filled disc, false = ring of width w */
    f: boolean
    w: number
}

export interface EmblemRect {
    t: EmblemOpType.Rect
    c: number
    /** top-left corner and size, in emblem space (0..255) */
    x: number
    y: number
    sx: number
    sy: number
    /** true = filled, false = outline of width w */
    f: boolean
    w: number
}

export type EmblemOp = EmblemStroke | EmblemCircle | EmblemRect;
export type Emblem = EmblemOp[];

/**
 * Colours players may NOT draw with: anything close to the ground, so nobody can paint
 * themselves into the grass or the sand. RGB of the battlefield palette (modes.ts, normal mode).
 */
const TERRAIN_RGB: ReadonlyArray<readonly [number, number, number, number]> = [
    // [r, g, b, radius]
    [86, 90, 58, 70],     // grass   hsl(67, 22%, 29%)
    [122, 105, 77, 70],   // beach   hsl(37, 23%, 39%)
    [151, 134, 107, 60],  // the uniform itself
    [80, 60, 48, 45],     // river bank hsl(30, 25%, 25%)
    [95, 70, 53, 45],     // trail   hsl(29, 28%, 29%)
    [75, 75, 75, 45],     // asphalt roads
    [54, 76, 84, 45]      // water   hsl(194, 22%, 27%)
];
/** hue bands (degrees) with lightness/saturation ranges that read as "ground" even when not an exact match */
const TERRAIN_BANDS: ReadonlyArray<readonly [number, number, number, number, number]> = [
    // [hue, hue tolerance, min lightness, max lightness, max saturation]
    [67, 30, 0.12, 0.42, 1.00],  // olive / yellow-greens, dark to mid
    [37, 22, 0.22, 0.58, 0.55],  // sand / khaki
    [30, 15, 0.10, 0.40, 0.60]   // mud browns
];

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b); const min = Math.min(r, g, b); const l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min; const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h *= 60;
    return [h, s, l];
}

/** true when a 24-bit colour is too close to the ground to be allowed in an emblem */
export function isBannedColor(c: number): boolean {
    const r = (c >> 16) & 255; const g = (c >> 8) & 255; const b = c & 255;
    const [h, s, l] = rgbToHsl(r, g, b);
    if (l <= 0.13 || l >= 0.92) return false; // near-black and near-white never blend into the ground
    for (const [tr, tg, tb, radius] of TERRAIN_RGB) {
        if (Math.hypot(r - tr, g - tg, b - tb) < radius) return true;
    }
    for (const [hue, tol, lmin, lmax, smax] of TERRAIN_BANDS) {
        const dh = Math.min(Math.abs(h - hue), 360 - Math.abs(h - hue));
        if (dh <= tol && l >= lmin && l <= lmax && s <= smax) return true;
    }
    return false;
}

const clampInt = (v: unknown, min: number, max: number): number | undefined => {
    if (typeof v !== "number" || !Number.isFinite(v)) return undefined;
    return Math.min(max, Math.max(min, Math.round(v)));
};

/**
 * Validates and normalises an emblem coming from an untrusted source (the client, or storage).
 * Returns undefined when there is nothing valid to keep.
 */
export function sanitizeEmblem(raw: unknown): Emblem | undefined {
    if (!Array.isArray(raw) || raw.length === 0) return undefined;
    const out: EmblemOp[] = [];
    let points = 0;
    for (const op of raw) {
        if (out.length >= EMBLEM.maxOps) break;
        if (typeof op !== "object" || op === null) continue;
        const o = op as Record<string, unknown>;
        const c = clampInt(o.c, 0, 0xffffff);
        if (c === undefined) continue;
        if (o.t !== EmblemOpType.Eraser && isBannedColor(c)) continue; // no ground colours
        if (o.t === EmblemOpType.Pencil || o.t === EmblemOpType.Eraser) {
            const w = clampInt(o.w, EMBLEM.minWidth, EMBLEM.maxWidth);
            if (w === undefined || !Array.isArray(o.p)) continue;
            const p: number[] = [];
            for (let i = 0; i + 1 < o.p.length && p.length < EMBLEM.maxPointsPerStroke * 2; i += 2) {
                const x = clampInt(o.p[i], 0, EMBLEM.size - 1); const y = clampInt(o.p[i + 1], 0, EMBLEM.size - 1);
                if (x === undefined || y === undefined) continue;
                p.push(x, y);
            }
            if (p.length < 2) continue;
            const room = (EMBLEM.maxPointsTotal - points) * 2;
            if (room <= 0) break;
            if (p.length > room) p.length = room;
            points += p.length / 2;
            out.push({ t: o.t, c, w, p });
        } else if (o.t === EmblemOpType.Circle) {
            const x = clampInt(o.x, 0, EMBLEM.size - 1); const y = clampInt(o.y, 0, EMBLEM.size - 1);
            const r = clampInt(o.r, 1, EMBLEM.maxRadius); const w = clampInt(o.w, EMBLEM.minWidth, EMBLEM.maxWidth) ?? 4;
            if (x === undefined || y === undefined || r === undefined) continue;
            out.push({ t: EmblemOpType.Circle, c, x, y, r, f: !!o.f, w });
        } else if (o.t === EmblemOpType.Rect) {
            const x = clampInt(o.x, 0, EMBLEM.size - 1); const y = clampInt(o.y, 0, EMBLEM.size - 1);
            const sx = clampInt(o.sx, 1, EMBLEM.size); const sy = clampInt(o.sy, 1, EMBLEM.size);
            const w = clampInt(o.w, EMBLEM.minWidth, EMBLEM.maxWidth) ?? 4;
            if (x === undefined || y === undefined || sx === undefined || sy === undefined) continue;
            out.push({ t: EmblemOpType.Rect, c, x, y, sx: Math.min(sx, EMBLEM.size - x), sy: Math.min(sy, EMBLEM.size - y), f: !!o.f, w });
        }
    }
    return out.length ? out : undefined;
}

/** stable string for texture caching */
export function emblemKey(emblem: Emblem | undefined): string {
    return emblem ? JSON.stringify(emblem) : "";
}

export function writeEmblem(stream: TrenchioByteStream, emblem: Emblem | undefined): void {
    const ops = sanitizeEmblem(emblem) ?? [];
    stream.writeUint8(Math.min(ops.length, EMBLEM.maxOps));
    for (const op of ops.slice(0, EMBLEM.maxOps)) {
        stream.writeUint8(op.t);
        stream.writeUint24(op.c);
        if (op.t === EmblemOpType.Circle) {
            stream.writeUint8(op.x); stream.writeUint8(op.y); stream.writeUint8(op.r);
            stream.writeUint8(op.f ? 1 : 0); stream.writeUint8(op.w);
        } else if (op.t === EmblemOpType.Rect) {
            stream.writeUint8(op.x); stream.writeUint8(op.y);
            stream.writeUint8(Math.min(255, op.sx)); stream.writeUint8(Math.min(255, op.sy));
            stream.writeUint8(op.f ? 1 : 0); stream.writeUint8(op.w);
        } else {
            stream.writeUint8(op.w);
            const n = Math.min(op.p.length >> 1, EMBLEM.maxPointsPerStroke);
            stream.writeUint16(n);
            for (let i = 0; i < n; i++) { stream.writeUint8(op.p[i * 2]); stream.writeUint8(op.p[i * 2 + 1]); }
        }
    }
}

export function readEmblem(stream: TrenchioByteStream): Emblem | undefined {
    const count = stream.readUint8();
    if (count === 0) return undefined;
    const ops: EmblemOp[] = [];
    for (let i = 0; i < count; i++) {
        const t = stream.readUint8(); const c = stream.readUint24();
        if (t === EmblemOpType.Circle) {
            const x = stream.readUint8(); const y = stream.readUint8(); const r = stream.readUint8();
            const f = stream.readUint8() === 1; const w = stream.readUint8();
            ops.push({ t: EmblemOpType.Circle, c, x, y, r, f, w });
        } else if (t === EmblemOpType.Rect) {
            const x = stream.readUint8(); const y = stream.readUint8(); const sx = stream.readUint8(); const sy = stream.readUint8();
            const f = stream.readUint8() === 1; const w = stream.readUint8();
            ops.push({ t: EmblemOpType.Rect, c, x, y, sx, sy, f, w });
        } else if (t === EmblemOpType.Pencil || t === EmblemOpType.Eraser) {
            const w = stream.readUint8(); const n = stream.readUint16();
            const p: number[] = [];
            for (let j = 0; j < n; j++) p.push(stream.readUint8(), stream.readUint8());
            ops.push({ t, c, w, p });
        } else {
            break; // unknown op type: stop reading rather than desync further
        }
    }
    return sanitizeEmblem(ops);
}
